/**
 * Own-seat policy for Phoenix seatmap paint + layout click gate.
 * Own = confirmed in selected passengers OR pending in lock tracker.
 */

/**
 * @param {string} seatId
 * @param {{ selected?: Array<{ seats?: Array<{ seatId?: string }> }>, pendingSeatId?: string|null }} ctx
 * @returns {boolean} true when WS/sync should paint the seat occupied (remote only)
 */
export function shouldPaintOccupied (seatId, { selected = [], pendingSeatId = null } = {}) {
  if (seatId == null || seatId === '') return false
  if (pendingSeatId != null && String(pendingSeatId) === String(seatId)) {
    return false
  }
  const inSelected = selected.some((passenger) =>
    (passenger.seats || []).some((seat) => String(seat.seatId) === String(seatId)),
  )
  if (inSelected) return false
  return true
}

/**
 * @param {Array<{ seat_id?: string, seatId?: string }>} syncSeats
 * @param {{ selected?: Array, pendingSeatId?: string|null }} ctx
 * @returns {string[]} remote seat ids to mark occupied
 */
export function remoteOccupiedSeatIds (syncSeats, ctx) {
  if (!Array.isArray(syncSeats)) return []
  return syncSeats
    .map((s) => s.seat_id ?? s.seatId)
    .filter((id) => id != null && shouldPaintOccupied(id, ctx))
}

/**
 * Click gate for BusLayout: open seats select; own selected seats deselect even if type is occupied.
 * @param {string} seatType
 * @param {boolean} isSelected
 */
export function shouldAcceptSeatClick (seatType, isSelected) {
  return seatType === 'open' || Boolean(isSelected)
}

/**
 * Apply per-seat type overrides without a global toggle.
 * @param {Array} busLayout
 * @param {Record<string, string>} overrides seatId → type
 */
export function applySeatTypeOverrides (busLayout, overrides) {
  if (!Array.isArray(busLayout) || !overrides || Object.keys(overrides).length === 0) {
    return busLayout
  }
  return busLayout.map((section) => ({
    ...section,
    rows: (section.rows || []).map((row) => ({
      ...row,
      seats: (row.seats || []).map((seat) => {
        const nextType = overrides[seat.id]
        return nextType != null ? { ...seat, type: nextType } : seat
      }),
    })),
  }))
}

/**
 * @param {Array} selected
 * @param {string} seatId
 * @param {string} [seatType='departure']
 */
export function deselectPassengerSeat (selected, seatId, seatType = 'departure') {
  if (!Array.isArray(selected)) return []
  return selected
    .map((passenger) => {
      const seats = (passenger.seats || []).filter(
        (s) => !(String(s.seatId) === String(seatId) && s.type === seatType),
      )
      if (seatType === 'return') {
        if (seats.length === 0) return null
        return { ...passenger, seats }
      }
      // departure deselect removes passenger (matches BookingBus)
      const stillHasDeparture = seats.some((s) => s.type === 'departure')
      if (!stillHasDeparture && seatType === 'departure') return null
      return { ...passenger, seats }
    })
    .filter(Boolean)
}

/**
 * @param {Array} selected
 * @param {{ fareId: string, seats: Array }} passenger
 */
export function confirmPassengerSeat (selected, passenger) {
  return [...(selected || []), passenger]
}

/**
 * Same contract as BookingBus verifyCountFares (footer Continuar).
 * @param {Record<string, number>} countFares
 * @param {Array<{ fareId?: string }>} selected
 */
export function verifyCountFares (countFares, selected) {
  const selectedCount = Object.keys(countFares).reduce((acc, fareId) => {
    acc[fareId] = 0
    return acc
  }, {})

  ;(selected || []).forEach(({ fareId }) => {
    if (Object.prototype.hasOwnProperty.call(selectedCount, fareId)) {
      selectedCount[fareId]++
    }
  })

  return Object.keys(countFares).every(
    (fareId) => countFares[fareId] === selectedCount[fareId],
  )
}
